import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Scale, Plus, Trash2, Save, ArrowDown, Info, AlertTriangle } from 'lucide-react';

const ACTIONS = [
  { value: 'present', label: 'Mark as Present', color: 'bg-green-500/10 text-green-600' },
  { value: 'late', label: 'Mark as Late', color: 'bg-yellow-500/10 text-yellow-600' },
  { value: 'warning', label: 'Issue Warning', color: 'bg-orange-500/10 text-orange-600' },
  { value: 'warning_then_halfday', label: 'Warning (1st) / Half-Day (2nd+)', color: 'bg-red-500/10 text-red-600' },
  { value: 'half_day', label: 'Mark as Half Day', color: 'bg-red-500/10 text-red-600' },
  { value: 'absent', label: 'Mark as Absent', color: 'bg-red-700/10 text-red-700' },
];

function getActionInfo(val) {
  return ACTIONS.find(a => a.value === val) || ACTIONS[0];
}

export default function LateRulesPage() {
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchRules();
  }, []);

  async function fetchRules() {
    setLoading(true);
    try {
      const res = await api.get('/hr/settings/late-rules');
      setRules(res.data);
    } catch {
      toast.error('Failed to load late rules');
    } finally {
      setLoading(false);
    }
  }

  function updateTier(index, field, value) {
    setRules(prev => {
      const tiers = [...(prev.tiers || [])];
      tiers[index] = { ...tiers[index], [field]: value };
      return { ...prev, tiers };
    });
  }

  function addTier() {
    const tiers = rules?.tiers || [];
    const lastMax = tiers.length > 0 ? tiers[tiers.length - 1].max_late + 1 : 0;
    setRules(prev => ({
      ...prev,
      tiers: [...(prev.tiers || []), {
        id: `tier_${Date.now()}`,
        name: '',
        min_late: lastMax,
        max_late: lastMax + 30,
        action: 'late',
        description: '',
      }]
    }));
  }

  function removeTier(index) {
    setRules(prev => ({
      ...prev,
      tiers: prev.tiers.filter((_, i) => i !== index),
    }));
  }

  async function handleSave() {
    if (!rules?.tiers?.length) { toast.error('Add at least one rule tier'); return; }
    // Validate
    for (let i = 0; i < rules.tiers.length; i++) {
      const t = rules.tiers[i];
      if (!t.name?.trim()) { toast.error(`Tier ${i + 1}: Name is required`); return; }
      if (t.min_late === undefined || t.max_late === undefined) { toast.error(`Tier ${i + 1}: Min/Max late required`); return; }
      if (t.min_late > t.max_late) { toast.error(`Tier ${i + 1}: Min late cannot be greater than max late`); return; }
    }
    setSaving(true);
    try {
      await api.put('/hr/settings/late-rules', {
        tiers: rules.tiers,
        extra_time_compensation: rules.extra_time_compensation ?? true,
        extra_time_threshold_minutes: rules.extra_time_threshold_minutes ?? 30,
      });
      toast.success('Late rules saved successfully');
      fetchRules();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save late rules');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="late-rules-page">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const tiers = rules?.tiers || [];

  return (
    <div className="space-y-6" data-testid="late-rules-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Late Rules Configuration</h1>
          <p className="text-muted-foreground text-sm mt-1">Define how late arrivals are classified and what action is taken at each tier</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-rules-btn">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Rules'}
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-blue-500/20 bg-blue-500/5">
        <CardContent className="py-4 flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-blue-600 mb-1">How Late Rules Work</p>
            <p className="text-muted-foreground">When an employee punches in, the system calculates how many minutes late they are (check-in time minus shift start time). It then matches against these tiers from top to bottom and applies the corresponding action. Each employee's shift grace period is also considered.</p>
          </div>
        </CardContent>
      </Card>

      {/* Rule Tiers */}
      <div className="space-y-3">
        {tiers.map((tier, i) => {
          const actionInfo = getActionInfo(tier.action);
          return (
            <Card key={tier.id || i} data-testid={`late-tier-${i}`} className="relative">
              <CardContent className="py-4">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                  {/* Tier Number */}
                  <div className="md:col-span-1 flex items-center justify-center">
                    <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center font-bold text-sm">
                      {i + 1}
                    </div>
                  </div>
                  {/* Name */}
                  <div className="md:col-span-2">
                    <Label className="text-xs">Rule Name</Label>
                    <Input
                      value={tier.name || ''}
                      onChange={e => updateTier(i, 'name', e.target.value)}
                      placeholder="e.g. Within Grace"
                      data-testid={`tier-name-${i}`}
                    />
                  </div>
                  {/* Min/Max */}
                  <div className="md:col-span-1">
                    <Label className="text-xs">From (min)</Label>
                    <Input
                      type="number"
                      min={0}
                      value={tier.min_late ?? 0}
                      onChange={e => updateTier(i, 'min_late', parseInt(e.target.value) || 0)}
                      data-testid={`tier-min-${i}`}
                    />
                  </div>
                  <div className="md:col-span-1">
                    <Label className="text-xs">To (min)</Label>
                    <Input
                      type="number"
                      min={0}
                      value={tier.max_late ?? 0}
                      onChange={e => updateTier(i, 'max_late', parseInt(e.target.value) || 0)}
                      data-testid={`tier-max-${i}`}
                    />
                  </div>
                  {/* Action */}
                  <div className="md:col-span-3">
                    <Label className="text-xs">Action</Label>
                    <Select value={tier.action} onValueChange={v => updateTier(i, 'action', v)}>
                      <SelectTrigger data-testid={`tier-action-${i}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ACTIONS.map(a => (
                          <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Description */}
                  <div className="md:col-span-3">
                    <Label className="text-xs">Description</Label>
                    <Input
                      value={tier.description || ''}
                      onChange={e => updateTier(i, 'description', e.target.value)}
                      placeholder="Optional description"
                      data-testid={`tier-desc-${i}`}
                    />
                  </div>
                  {/* Delete */}
                  <div className="md:col-span-1 flex justify-end">
                    <Button size="icon" variant="ghost" className="text-destructive h-9 w-9" onClick={() => removeTier(i)} data-testid={`tier-delete-${i}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {/* Action Preview Badge */}
                <div className="mt-2 ml-14">
                  <Badge className={actionInfo.color + ' text-xs'}>
                    {tier.min_late}-{tier.max_late} min late: {actionInfo.label}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          );
        })}

        <Button variant="outline" className="w-full border-dashed" onClick={addTier} data-testid="add-tier-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Rule Tier
        </Button>
      </div>

      {/* Extra Time Compensation */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Extra Time Compensation</CardTitle>
          <CardDescription>If enabled, employees who are late but stay past their shift end can have their late status forgiven.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Enable Extra Time Compensation</p>
              <p className="text-xs text-muted-foreground">Late employees who stay extra can be marked as present</p>
            </div>
            <Switch
              checked={rules?.extra_time_compensation ?? true}
              onCheckedChange={v => setRules(prev => ({ ...prev, extra_time_compensation: v }))}
              data-testid="extra-time-switch"
            />
          </div>
          {rules?.extra_time_compensation && (
            <div className="flex items-center gap-3">
              <Label className="text-sm whitespace-nowrap">Minimum extra minutes required:</Label>
              <Input
                type="number"
                min={0}
                className="w-24"
                value={rules?.extra_time_threshold_minutes ?? 30}
                onChange={e => setRules(prev => ({ ...prev, extra_time_threshold_minutes: parseInt(e.target.value) || 0 }))}
                data-testid="extra-time-threshold"
              />
              <span className="text-sm text-muted-foreground">minutes past shift end</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
